/**
 * Alpine component for handling quiz logic in experience sections.
 */
import experienceQuizConfig from '../config/experienceQuiz.js';

const QUIZ_OVERLAY_DELAY_MS = 2300; // 2300

const shuffleOptions = (options) => {
    const result = [...options];

    for (let index = result.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        const current     = result[index];

        result[index]       = result[randomIndex];
        result[randomIndex] = current;
    }

    return result;
};

const getOptionLabelLength = (option) => {
    if (!option || typeof option.label !== 'string') {
        return 0;
    }

    return option.label.replace(/\s+/g, ' ').trim().length;
};

const shuffleOptionsForSection = (_sectionId, options) => {
    if (!Array.isArray(options) || options.length !== 4) {
        return shuffleOptions(options);
    }

    const byLengthDesc = [...options].sort((left, right) => {
        const rightLength = getOptionLabelLength(right);
        const leftLength  = getOptionLabelLength(left);

        return rightLength - leftLength;
    });

    const longRow            = byLengthDesc.slice(0, 2);
    const shortRow           = byLengthDesc.slice(2, 4);
    const shuffledLongRow    = shuffleOptions(longRow);
    const shuffledShortRow   = shuffleOptions(shortRow);
    const isLongRowOnTop     = Math.random() >= 0.5;

    return isLongRowOnTop
        ? [...shuffledLongRow, ...shuffledShortRow]
        : [...shuffledShortRow, ...shuffledLongRow];
};

export default function experienceSectionQuiz(sectionId) {
    return () => ({
        sectionId,
        question               : '',
        options                : [],
        correctId              : null,
        buttonText             : '',
        errorMessage           : '',
        successMessage         : '',
        successTitle           : '',
        successText            : '',
        selectedOptionId       : null,
        isAnswered             : false,
        isCorrect              : false,
        isResultOverlayVisible : false,
        isShowOptions          : false,
        overlayTimeoutId       : null,

        init() {
            try {
                const config = experienceQuizConfig[this.sectionId];

                if (!config) {
                    return;
                }

                this.question       = config.question;
                this.options        = shuffleOptionsForSection(this.sectionId, config.options);
                this.correctId      = config.correctId;
                this.buttonText     = config.buttonText;
                this.errorMessage   = config.errorMessage;
                this.successMessage = config.successMessage || '';
                this.successTitle   = config.successTitle;
                this.successText    = config.successText;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('experienceSectionQuiz init error', error);
            }
        },

        selectOption(optionId) {
            if (this.isAnswered) {
                return;
            }

            this.selectedOptionId = optionId;
            this.isAnswered       = true;
            this.isCorrect        = optionId === this.correctId;

            if (this.overlayTimeoutId) {
                window.clearTimeout(this.overlayTimeoutId);
                this.overlayTimeoutId = null;
            }

            this.overlayTimeoutId = window.setTimeout(() => {
                this.isResultOverlayVisible = true;

                this.$nextTick(() => {
                    const rootElement = this.$root;

                    if (!rootElement) {
                        return;
                    }

                    const scrollElement = rootElement.querySelector('.experience-quiz-result__text[data-simplebar]');

                    if (!scrollElement) {
                        return;
                    }

                    if (typeof window === 'undefined' || !window.SimpleBar || !window.SimpleBar.instances) {
                        return;
                    }

                    const instance = window.SimpleBar.instances.get(scrollElement);
                    if (!instance || typeof instance.recalculate !== 'function') {
                        return;
                    }

                    instance.recalculate();
                });

                window.dispatchEvent(new CustomEvent('experience-quiz:overlay-shown', {
                    detail : {
                        sectionId : this.sectionId,
                        isCorrect : this.isCorrect
                    }
                }));
            }, QUIZ_OVERLAY_DELAY_MS);
        },

        getOptionClasses(optionId) {
            if (!this.isAnswered) {
                return {};
            }

            const isOptionCorrect  = optionId === this.correctId;
            const isOptionSelected = optionId === this.selectedOptionId;

            return {
                'experience-quiz__option--correct-selected' : this.isCorrect && isOptionSelected,
                'experience-quiz__option--wrong-selected'   : !this.isCorrect && isOptionSelected && !isOptionCorrect,
                'experience-quiz__option--correct-actual'   : !this.isCorrect && isOptionCorrect
            };
        },

        handleContinue() {
            if (!this.isAnswered) {
                return;
            }

            window.dispatchEvent(new CustomEvent('experience-quiz:continue', {
                detail : {
                    sectionId : this.sectionId,
                    isCorrect : this.isCorrect
                }
            }));
        },

        handleOptionsTrigger() {
            this.isShowOptions = !this.isShowOptions;
        }
    });
}
